import type { ExperienceLocation } from "@/types/generated/MetadataExperiencesApi";

export interface AddressFieldValues {
  latitude?: number;
  longitude?: number;
  direction: string;
  postalCode: string;
}

export type ExperienceLocationData = Omit<ExperienceLocation, "experience_id">;

export type LocationType = "ADDRESS" | "COORDINATES";

export const LOCATION_TYPE_OPTIONS: { label: string; value: LocationType }[] = [
  { label: "Address", value: "ADDRESS" },
  { label: "Coordinates", value: "COORDINATES" },
];

export function areValidCoordinates(latitude?: number | null, longitude?: number | null): boolean {
  return Number.isFinite(latitude) && Number.isFinite(longitude);
}

export function hasCoordinates(address: AddressFieldValues): boolean {
  return areValidCoordinates(address.latitude, address.longitude);
}

export function getEmptyAddress(): AddressFieldValues {
  return {
    latitude: undefined,
    longitude: undefined,
    direction: "",
    postalCode: "",
  };
}

/**
 * Builds the location payload for saving to the raw experience API.
 */
export function buildLocationPayload(
  city: { name: string; code: string } | null,
  address: AddressFieldValues,
  locationType: LocationType
): ExperienceLocationData | undefined {
  if (!city) return undefined;

  return {
    name: city.name,
    ...(areValidCoordinates(address.latitude, address.longitude) && {
      latitude: address.latitude!.toString(),
      longitude: address.longitude!.toString(),
    }),
    address: {
      city: city.code,
      ...(locationType === "ADDRESS" && address.postalCode && { postal_code: address.postalCode }),
      ...(locationType === "ADDRESS" && address.direction && { direction: address.direction }),
    },
  };
}

/**
 * Builds the additional cities payload (array of city codes).
 */
export function buildAdditionalCitiesPayload(cities: { code: string }[]): string[] {
  return cities.map((city) => city.code);
}
